// My Contacts — cross-project contact list
import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Search, Users, Plus, Trash2 } from 'lucide-react'
import { getMyContacts, createCrmContact, deleteCrmContact, getProjects } from '../../../services/api'
import { disambiguateProjectLabels } from '../../../utils/projects'
import type { CrmContact, Project } from '../../../types'
import PageHeader from '../../../components/ui/PageHeader'
import EmptyState from '../../../components/ui/EmptyState'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'
import CrmLoadError from '../../../components/crm/CrmLoadError'
import CrmTabs from '../../../components/crm/CrmTabs'
import ContactName from '../../../components/crm/ContactName'
import { contactDisplayName, contactInitials } from '../../../utils/crmPipeline'
import { projectLabel } from '../../../components/crm/crmProjects'

export default function MyContacts() {
  const navigate = useNavigate()
  const [contacts, setContacts] = useState<CrmContact[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [pendingDelete, setPendingDelete] = useState<CrmContact | null>(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [orgName, setOrgName] = useState('')
  const [projectId, setProjectId] = useState('')

  const load = () => {
    setLoading(true)
    setLoadError(false)
    // A failed request must show an error with Retry, never an empty list (UX-038).
    Promise.all([getMyContacts(), getProjects()])
      .then(([contactData, projectData]) => {
        setContacts(contactData.contacts || [])
        const projectList = Array.isArray(projectData) ? projectData : []
        setProjects(projectList)
        if (projectList.length > 0 && !projectId) setProjectId(projectList[0].project_id)
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const filtered = contacts.filter((c) => {
    const q = search.toLowerCase()
    return !q || [c.first_name, c.last_name, c.email, c.organisation_name].filter(Boolean).join(' ').toLowerCase().includes(q)
  })

  const resetForm = () => {
    setFirstName(''); setLastName(''); setEmail(''); setOrgName(''); setError('')
    setShowForm(false)
  }

  const handleCreate = async () => {
    if (!firstName.trim() || !lastName.trim()) return
    if (!projectId) { setError('Please select a project'); return }
    setSaving(true)
    setError('')
    try {
      const contact = await createCrmContact({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        organisation_name: orgName.trim(),
        project_id: projectId,
      })
      setContacts((prev) => [contact, ...prev])
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contact')
    } finally {
      setSaving(false)
    }
  }

  // Honest delete (P7): the real mutation runs inside the ConfirmDialog. On
  // failure it throws so the dialog stays open and surfaces the message.
  const confirmDelete = async () => {
    if (!pendingDelete) return
    await deleteCrmContact(pendingDelete.contact_id, pendingDelete.project_id)
    setContacts((prev) => prev.filter((x) => x.contact_id !== pendingDelete.contact_id))
  }

  return (
    <div className="space-y-4">
      <Link to="/crm" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={16} /> Back to CRM
      </Link>

      <CrmTabs />

      <PageHeader
        title="My Contacts"
        description="Every contact you've added across your projects, in one place."
        helpKey="crm"
        status={loadError || loading ? undefined : { label: `(${filtered.length})` }}
        primaryAction={
          <button
            onClick={() => { setError(''); setShowForm(true) }}
            className="flex items-center gap-1.5 px-3 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 transition-colors"
          >
            <Plus size={16} /> Add Contact
          </button>
        }
      />

      {showForm && (
        <div className="light-card p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">New Contact</h3>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}
          <div className="grid grid-cols-2 gap-3">
            <input type="text" placeholder="First name *" value={firstName} onChange={(e) => setFirstName(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
            <input type="text" placeholder="Last name *" value={lastName} onChange={(e) => setLastName(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
            <input type="text" placeholder="Organisation" value={orgName} onChange={(e) => setOrgName(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          {projects.length === 0 ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              A contact belongs to a project. <Link to="/projects" className="font-medium underline">Create a project first</Link>, then come back to add contacts.
            </p>
          ) : (
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              aria-label="Project"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
            >
              <option value="">Select a project *</option>
              {disambiguateProjectLabels(projects).map((p) => (
                <option key={p.project_id} value={p.project_id}>{p.displayName}</option>
              ))}
            </select>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={resetForm} className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            <button onClick={handleCreate} disabled={!firstName.trim() || !lastName.trim() || !projectId || saving}
              className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 disabled:opacity-50 transition-colors">
              {saving ? 'Saving...' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
        />
      </div>

      {/* Content */}
      {loadError ? (
        <CrmLoadError what="your contacts" onRetry={load} />
      ) : loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 bg-sky-500/10 rounded-lg" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title={search ? 'No contacts match your search' : 'No contacts yet'}
          description={search
            ? 'Try a different name or email.'
            : 'Contacts you add to any project appear here, across your whole portfolio.'}
          action={search ? undefined : (
            <button
              onClick={() => { setError(''); setShowForm(true) }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600"
            >
              <Plus size={16} /> Add your first contact
            </button>
          )}
        />
      ) : (
        <div className="light-card overflow-hidden">
          <table className="min-w-full divide-y divide-sky-200/50">
            <thead className="bg-sky-500/5">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organisation</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tags</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sky-200/30">
              {filtered.map((c) => (
                <tr
                  key={c.contact_id}
                  onClick={(e) => {
                    // Mouse convenience only: the name is a real link for keyboard users.
                    if ((e.target as HTMLElement).closest('a,button')) return
                    navigate(`/project/${c.project_id}/crm/contacts/${c.contact_id}`)
                  }}
                  className="hover:bg-white/30 cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-sky-500/10 rounded-full flex items-center justify-center text-xs font-semibold text-sky-700 shrink-0">
                        {contactInitials(c)}
                      </div>
                      <Link
                        to={`/project/${c.project_id}/crm/contacts/${c.contact_id}`}
                        className="text-sm font-medium text-gray-900 hover:text-sky-700 hover:underline"
                      >
                        <ContactName contact={c} />
                      </Link>
                      {c.source === 'author-conversion' && (
                        <span className="text-xs bg-amber-500/20 text-amber-700 px-2 py-0.5 rounded-full">Author</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{c.email || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{c.organisation_name || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{projectLabel(projects, c.project_id)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(c.tags || []).slice(0, 3).map((tag) => (
                        <span key={tag} className="text-xs bg-sky-500/10 text-gray-600 px-2 py-0.5 rounded-full">{tag}</span>
                      ))}
                      {(c.tags || []).length > 3 && (
                        <span className="text-xs text-gray-500">+{c.tags.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); setPendingDelete(c) }}
                      className="text-gray-600 hover:text-red-500 transition-colors"
                      title="Delete contact"
                      aria-label={`Delete contact ${contactDisplayName(c)}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title="Delete contact?"
        message={
          pendingDelete
            ? `This permanently removes ${contactDisplayName(pendingDelete)} from its project. This can't be undone.`
            : ''
        }
        confirmLabel="Delete contact"
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  )
}
